import React, { useMemo } from 'react';
import { useMount } from 'react-use';
import dayjs from 'dayjs';
import ceil from 'lodash/ceil';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Progress,
  StackDivider,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Plus, X } from 'react-feather';
import { DiscountType } from '@olo-web/domain/orders/types';
import { getDiscountType } from '@olo-web/domain/customer/functions';
import { BusinessDeals } from '@olo-web/assets/icons/BusinessDeals.ui';
import { SpotOnO } from '@olo-web/assets/icons/SpotOnO.ui';
import {
  useApplyOrRemoveOffer,
  useHasLoyaltyBeenAddedByOtherGuest,
  useLoyalty,
} from '@olo-web/domain/customer';
import { RewardEmbellishment } from '@olo-web/assets/images/RewardEmbellishment.ui';
import { LockImage } from '@olo-web/assets/images/LockImage.ui';
import { useOffersHelpers } from '@organisms/Modals/ApplyOffersModal/hooks';
import { useAlertDispatch, useScreenSizeState } from '@olo-web/client-state';
import { EAlertTypes } from '@olo-web/types/enums';
import { useIsAccountPage, useToast } from '@olo-web/utils/common/hooks';
import { useCombinedTheme } from '@olo-web/theme/hooks';
import { useMerchant } from '@domain/merchants/queries/useMerchant';
import { getDealDiscountData } from '@olo-web/utils/common/functions/getDealDiscountDescription';
import { ILoyaltyItem, IPromoCodeDiscount } from '@olo-web/types/loyaltyReward.interface';

interface ILoyaltyCardProps {
  loyaltyItem: ILoyaltyItem | IPromoCodeDiscount;
  isActive?: boolean;
  isFullWidth?: boolean;
  scrollToBannerAndShakeIt?: () => void;
}

interface IItemUiSetting {
  color: string;
  icon: JSX.Element;
  label: string;
  description: JSX.Element;
  inactiveDescription: JSX.Element;
  title?: string;
  inactiveIcon?: JSX.Element;
  progressText?: string | JSX.Element;
  progressValue?: number;
}

const formatMinPurchaseAmount = (num: number): string | number =>
  num && Number.isInteger(num) ? num : ceil(num, 2);

const TagCustom = ({ icon, label, borderColor, textColor = 'black' }) => (
  <Tag
    size="md"
    variant="outline"
    borderColor={borderColor}
    borderRadius={0}
    border="none"
    boxShadow="none"
    padding={0}
    aria-hidden="true"
  >
    <Divider
      borderColor={borderColor}
      height={4}
      width="auto"
      borderRight="1px solid"
      borderBottom={0}
      margin="0 0.75rem 0 0"
    />
    <TagLeftIcon boxSize={4}>{icon}</TagLeftIcon>
    <TagLabel color={textColor} fontSize="xs" lineHeight="1.25rem" fontWeight="400">
      {label}
    </TagLabel>
  </Tag>
);

export const LoyaltyCard = (props: ILoyaltyCardProps) => {
  const { loyaltyItem, isActive, isFullWidth, scrollToBannerAndShakeIt } = props;
  const { order, handleOfferSelection, appliedOffer, itemTotal } = useOffersHelpers();
  const { applyOffer, removeOffer } = useApplyOrRemoveOffer();
  const hasLoyaltyBeenAddedByOtherGuest = useHasLoyaltyBeenAddedByOtherGuest();
  const { isSmDown } = useScreenSizeState();
  const isAccountPage = useIsAccountPage();
  const alertDispatch = useAlertDispatch();
  const { colors } = useCombinedTheme();
  const discountType = getDiscountType(loyaltyItem);
  const { data: merchant } = useMerchant();
  const { data: loyalty } = useLoyalty();
  const preDiscountSubtotal = (parseFloat(order?.preDiscountSubtotal ?? '') * 100) / 100;
  const minPurchaseAmount = Number((loyaltyItem as ILoyaltyItem).minPurchaseAmount);
  const { notify } = useToast();
  const expireDateInLocalTz =
    (loyaltyItem as ILoyaltyItem).promotionEndAt &&
    dayjs((loyaltyItem as ILoyaltyItem).promotionEndAt)
      .tz(merchant!.locationTimezone)
      .format('MM/DD/YY');

  const isMarketingCodeDeal = 'minOrderValue' in loyaltyItem || 'isAdHocDeal' in loyaltyItem;

  const dealDiscountData = isMarketingCodeDeal
    ? getDealDiscountData(
        loyaltyItem.adjustmentKind,
        Number(loyaltyItem.adjustmentValue),
        Number(loyaltyItem.minOrderValue || 0) || undefined,
        Number(loyaltyItem.maxDiscountValue || 0) || undefined
      )
    : undefined;

  const items: { [key: string]: IItemUiSetting } = useMemo(
    () => ({
      [DiscountType.REWARD]: {
        color: colors.loyalty[400],
        icon: <SpotOnO color="loyalty.400" />,
        label: 'Reward',
        description: (
          <HStack role="text">
            <Text textStyle="p">Redeem for:</Text>
            <Text textStyle="pBold">{loyaltyItem.spotsToRedeem} Spots</Text>
          </HStack>
        ),
        inactiveDescription: (
          <HStack role="text">
            <Text textStyle="p">Redeem for:</Text>
            <Text textStyle="pBold">{loyaltyItem.spotsToRedeem} Spots</Text>
          </HStack>
        ),
        inactiveIcon: <SpotOnO color="blackAlpha.350" />,
        progressText: `${parseInt(loyalty?.availableSpots ?? '')}/${
          loyaltyItem?.spotsToRedeem
        } Spots`,
        progressValue:
          (parseInt(loyalty?.availableSpots ?? '') /
            parseInt(loyaltyItem?.spotsToRedeem?.toString() ?? '')) *
          100,
      },
      [DiscountType.PROMO_CODE]: {
        color: colors.primary[300],
        icon: <BusinessDeals color="primary.350" />,
        label: 'Promotion',
        title: dealDiscountData?.title || loyaltyItem.promotionName,
        description: (
          <HStack role="text">
            {!dealDiscountData ? (
              <>
                <Text textStyle="p">Code:</Text>
                <Text textStyle="pBold">{(loyaltyItem as IPromoCodeDiscount).promoCode}</Text>
              </>
            ) : (
              <Text textStyle="p">{dealDiscountData.description}</Text>
            )}
          </HStack>
        ),
        inactiveDescription: (
          <Text textStyle="p">
            {!dealDiscountData
              ? (loyaltyItem as IPromoCodeDiscount).promoCode
              : dealDiscountData?.description}
          </Text>
        ),
        inactiveIcon: <BusinessDeals color="blackAlpha.350" />,
        progressText: `Spend $${(isMarketingCodeDeal
          ? Number(loyaltyItem?.minOrderValue) - itemTotal
          : 0
        ).toFixed(2)} more to unlock`,
        progressValue:
          (itemTotal * 100) / (isMarketingCodeDeal ? Number(loyaltyItem?.minOrderValue) : 0),
      },
      [DiscountType.DEAL]: {
        color: colors.primary[300],
        icon: <BusinessDeals color="primary.350" />,
        label: 'Promotion',
        description: (
          <Flex justify="space-between" w="100%" role="text">
            <Text textStyle="p">
              {minPurchaseAmount
                ? `Orders of $${formatMinPurchaseAmount(minPurchaseAmount)}+`
                : 'All orders'}
            </Text>
            {(loyaltyItem as ILoyaltyItem).promotionEndAt && (
              <Text as="i" textStyle="p" fontSize="xs">
                Expires {expireDateInLocalTz}
              </Text>
            )}
          </Flex>
        ),
        inactiveDescription: (
          <Text textStyle="p">Orders of ${formatMinPurchaseAmount(minPurchaseAmount)}+</Text>
        ),
        inactiveIcon: <BusinessDeals color="blackAlpha.350" />,
        progressText: `Spend ${(minPurchaseAmount - preDiscountSubtotal).toFixed(
          2
        )} more to unlock`,
        progressValue: (preDiscountSubtotal / minPurchaseAmount) * 100,
      },
    }),
    [
      preDiscountSubtotal,
      minPurchaseAmount,
      loyaltyItem,
      colors.loyalty,
      colors.primary,
      expireDateInLocalTz,
      loyalty?.availableSpots,
    ]
  );

  // If user removes everything from their bag and comes back to the account page,
  // the reward should be removed from the bag as well.
  useMount(() => {
    if ((!order?.items || order?.items?.length === 0) && appliedOffer) {
      removeOffer(appliedOffer);
    }
  });

  const itemUiSetting = items[discountType];

  const isAppliedOffer =
    discountType === DiscountType.PROMO_CODE
      ? 'tempPromotionId' in loyaltyItem
        ? appliedOffer?.spotOnRewardId === loyaltyItem.promotionId
        : appliedOffer?.id === loyaltyItem.promotionId
      : appliedOffer?.spotOnRewardId === loyaltyItem.promotionId;

  const offerExpiredCheck = () => {
    if (
      dayjs(order?.orderDateTime).diff(loyaltyItem?.promotionEndAt) > 0 &&
      discountType === DiscountType.DEAL
    ) {
      notify({
        description: `Sorry, the ${loyaltyItem?.promotionName} promotion can only be applied to orders scheduled for ${expireDateInLocalTz} or earlier.`,
        variant: 'left-accent',
        isClosable: true,
      });
      return true;
    }
  };

  const onApplyOfferWithAlerts = () => {
    if (offerExpiredCheck()) return;
    handleOfferSelection(loyaltyItem);
    if (!order?.items || order?.items?.length === 0) {
      alertDispatch({
        type: 'OPEN_ALERT',
        payload: {
          alertKey: EAlertTypes.REWARD_TO_AN_EMPTY_BAG_ALERT,
        },
      });
      return;
    }
    if (appliedOffer && appliedOffer.spotOnRewardId !== loyaltyItem.promotionId) {
      alertDispatch({
        type: 'OPEN_ALERT',
        payload: {
          alertKey: EAlertTypes.REWARDS_CAPACITY_ALERT,
        },
      });
      return;
    }
    applyOffer(loyaltyItem);
  };

  const onApplyOfferWithoutAlerts = () => {
    if (offerExpiredCheck()) return;
    applyOffer(loyaltyItem);
  };

  const onRemoveOffer = () => {
    removeOffer(loyaltyItem);
  };

  if (!isActive) {
    return (
      <Box
        px={4}
        py={2}
        w={isSmDown || isFullWidth || !isAccountPage ? '100%' : '364px'}
        minH="103px"
        borderRadius={4}
        border="1px solid"
        borderColor="blackAlpha.200"
        overflow="hidden"
      >
        <HStack color="blackAlpha.350" spacing={2} h="100%">
          <LockImage w="42px" h="50px" />
          <VStack align="start" spacing={1} width="100%" h="100%">
            <HStack
              spacing={3}
              divider={<StackDivider borderColor="blackAlpha.300" />}
              flexGrow={1}
              alignItems="top"
            >
              <Text as="h4" textStyle="h4" fontSize={'14px'}>
                <Text as="span" mr="0.75rem">
                  {itemUiSetting.title || loyaltyItem.promotionName}
                </Text>
                <TagCustom
                  icon={itemUiSetting?.inactiveIcon}
                  label={itemUiSetting.label}
                  borderColor="blackAlpha.350"
                  textColor="blackAlpha.350"
                />
              </Text>
            </HStack>
            <Text fontSize={'14px'}>{itemUiSetting?.inactiveDescription}</Text>
            <Text as="i" fontSize="xs" mt="0.5rem !important">
              {itemUiSetting?.progressText}
            </Text>
            <Progress
              value={itemUiSetting?.progressValue}
              size="sm"
              colorScheme="secondary"
              backgroundColor="white"
              border="0.5px solid #949494"
              width="100%"
              mt={0}
              borderRadius="25px"
              height="6px"
            />
          </VStack>
        </HStack>
      </Box>
    );
  }
  return (
    <Box
      p={2}
      pr={4}
      w={isSmDown || isFullWidth || !isAccountPage ? '100%' : '364px'}
      minH="103px"
      borderRadius={4}
      border="1px solid"
      borderColor={isAppliedOffer ? `${itemUiSetting?.color}` : 'blackAlpha.300'}
      overflow="hidden"
      _hover={{
        borderColor: `${itemUiSetting?.color}`,
      }}
      borderLeft="4px solid"
      borderLeftColor={itemUiSetting?.color}
      // check below change if there is a problem
      background={
        isAppliedOffer
          ? `linear-gradient(0deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), ${itemUiSetting.color}`
          : undefined
      }
    >
      <HStack spacing={4} h="100%" align="start">
        <RewardEmbellishment
          h="auto"
          maxHeight="100%"
          w="24px"
          currentColor={itemUiSetting.color}
          marginBottom="25px"
        />
        <VStack
          align="start"
          w="100%"
          h="100%"
          divider={<StackDivider borderColor="blackAlpha.200" />}
          spacing={2}
        >
          <VStack align="start" spacing={2} width="100%" flexGrow={1}>
            <HStack
              spacing={3}
              divider={<StackDivider borderColor="blackAlpha.300" />}
              flexGrow={1}
              alignItems="top"
            >
              <Text as="h4" textStyle="h4" role="text">
                <Text as="span" mr="0.75rem">
                  {itemUiSetting?.title || loyaltyItem.promotionName}
                </Text>
                <TagCustom
                  icon={itemUiSetting?.icon}
                  label={itemUiSetting.label}
                  borderColor="blackAlpha.300"
                  textColor="black"
                />
              </Text>
            </HStack>
            {itemUiSetting.description}
          </VStack>
          <Button
            data-testid="apply-reward-to-bag-button"
            textStyle="pBold"
            variant="link"
            w="100%"
            justifyContent="space-between"
            colorScheme="black"
            rightIcon={<Icon as={isAppliedOffer ? X : Plus} h={6} w={6} strokeWidth={2} />}
            onClick={
              hasLoyaltyBeenAddedByOtherGuest
                ? scrollToBannerAndShakeIt
                : isAppliedOffer
                ? onRemoveOffer
                : isAccountPage
                ? onApplyOfferWithAlerts
                : onApplyOfferWithoutAlerts
            }
          >
            {isAppliedOffer ? 'Remove from bag' : 'Apply to bag'}
          </Button>
        </VStack>
      </HStack>
    </Box>
  );
};
